import React from 'react';
import { Typography } from 'antd';
import {
  BellOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CloseCircleOutlined, DesktopOutlined, LoadingOutlined,
} from '@ant-design/icons';
import type { AgentStep, PlanStep } from '../types';
import DurationBadge from './DurationBadge';

const { Text } = Typography;

// ── Status indicators ─────────────────────────────────────────────────────────

const STATUS_DOT: Record<string, React.ReactNode> = {
  PENDING: <ClockCircleOutlined style={{ color: '#555', fontSize: 14 }} />,
  RUNNING: <LoadingOutlined style={{ color: '#3b82f6', fontSize: 14 }} />,
  COMPLETED: <CheckCircleOutlined style={{ color: '#10b981', fontSize: 14 }} />,
  FAILED: <CloseCircleOutlined style={{ color: '#ef4444', fontSize: 14 }} />,
  SKIPPED: <ClockCircleOutlined style={{ color: '#888', fontSize: 14 }} />,
  WAITING_FOR_APPROVAL: <BellOutlined style={{ color: '#faad14', fontSize: 14 }} />,
};

const STATUS_LABEL_COLOR: Record<string, string> = {
  PENDING: '#6b7280',
  RUNNING: '#3b82f6',
  COMPLETED: '#10b981',
  FAILED: '#ef4444',
  SKIPPED: '#888',
  WAITING_FOR_APPROVAL: '#faad14',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  steps: AgentStep[];
  planSteps?: PlanStep[];
  taskStatus: string;
  onStepClick?: (step: AgentStep) => void;
}

// ── Step card ─────────────────────────────────────────────────────────────────

const StepCard: React.FC<{ step: AgentStep; index: number; onViewClick?: (step: AgentStep) => void }> = ({
  step, index, onViewClick,
}) => {
  const statusColor = STATUS_LABEL_COLOR[step.status] || '#6b7280';

  return (
    <div className="praxys-step-card">
      <div className="praxys-step-icon">
        {STATUS_DOT[step.status] || <ClockCircleOutlined style={{ fontSize: 14, color: '#555' }} />}
      </div>

      <div className="praxys-step-info">
        <div className="praxys-step-name">
          <span className="praxys-step-num">{index + 1}.</span>
          {step.name}
          <span className="praxys-step-status-label" style={{ color: statusColor }}>
            {step.status === 'RUNNING' && ' · running…'}
            {step.status === 'COMPLETED' && ' · done'}
            {step.status === 'FAILED' && ' · failed'}
            {step.status === 'WAITING_FOR_APPROVAL' && ' · awaiting approval'}
          </span>
        </div>
        {step.description && (
          <div className="praxys-step-desc">{step.description}</div>
        )}
        {step.error_message && (
          <div className="praxys-step-error">⚠ {step.error_message}</div>
        )}
      </div>

      <div className="praxys-step-right">
        <DurationBadge startedAt={step.started_at} completedAt={step.completed_at} />
        {onViewClick && (
          <button
            className="praxys-step-view-btn"
            onClick={() => onViewClick(step)}
            title="View in Praxys's Computer"
          >
            <DesktopOutlined />
          </button>
        )}
      </div>
    </div>
  );
};

// ── Plan step preview (before execution) ─────────────────────────────────────

const PlanStepPreview: React.FC<{ step: PlanStep; index: number }> = ({ step, index }) => (
  <div className="praxys-step-card praxys-step-card--planned">
    <div className="praxys-step-icon">
      <ClockCircleOutlined style={{ color: '#555', fontSize: 14 }} />
    </div>
    <div className="praxys-step-info">
      <div className="praxys-step-name">
        <span className="praxys-step-num">{index + 1}.</span>
        {step.name}
      </div>
      {step.description && (
        <div className="praxys-step-desc">{step.description}</div>
      )}
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const AgentStepTimeline: React.FC<Props> = ({
  steps,
  planSteps,
  taskStatus,
  onStepClick,
}) => {
  const showPlanPreview = steps.length === 0 && (planSteps || []).length > 0;

  return (
    <div>
      {/* Planned steps preview */}
      {showPlanPreview && (
        <div style={{ marginBottom: 16 }}>
          <Text style={{ color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
            Planned Steps ({planSteps!.length})
          </Text>
          <div style={{ marginTop: 10 }}>
            {planSteps!.map((s, i) => (
              <PlanStepPreview key={s.id || i} step={s} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Execution steps */}
      {steps.length > 0 && (
        <div className="praxys-steps-list">
          {steps.map((step, i) => (
            <StepCard
              key={step.id}
              step={step}
              index={i}
              onViewClick={onStepClick}
            />
          ))}
        </div>
      )}

      {steps.length === 0 && !showPlanPreview && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#555' }}>
          <Text style={{ color: '#555', display: 'block', marginTop: 10, fontSize: 13 }}>
            {taskStatus === 'CREATED'
              ? 'Generate a plan to see steps'
              : taskStatus === 'PLANNING'
              ? 'Generating plan…'
              : 'No steps yet'}
          </Text>
        </div>
      )}

      {/* Bouncing-dot "working" indicator: visible when the task is RUNNING but
          no individual step is actively executing (e.g. agent is compiling the
          final answer after completing all steps). */}
      {taskStatus === 'RUNNING' && steps.every(s => s.status !== 'RUNNING') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0 4px' }}>
          <div className="praxys-typing-dots">
            <span /><span /><span />
          </div>
          <Text style={{ color: '#6b7280', fontSize: 12 }}>Working…</Text>
        </div>
      )}
    </div>
  );
};

export default AgentStepTimeline;
